package one.healthium.log_food.ui.scan_results.components

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.dropShadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.dp
import ifaandroid.feature.profile.log_food.generated.resources.Res
import ifaandroid.feature.profile.log_food.generated.resources.ic_keyboard_hide_24
import one.healthium.profile.ui.food.components.AmountPicker
import one.healthium.profile.ui.food.components.DatePicker
import one.healthium.profile.ui.food.models.AmountUiModel
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.painterResource
import kotlin.time.Instant

internal sealed interface PickerTarget {
    data object None : PickerTarget
    data object Date : PickerTarget
    data class Amount(val index: Int) : PickerTarget
}

@Composable
internal fun PickerPanel(
    visible: Boolean,
    onDoneClicked: () -> Unit,
    content: @Composable () -> Unit,
) {
    AnimatedVisibility(
        visible = visible,
        enter = expandVertically(expandFrom = Alignment.Bottom) + fadeIn(),
        exit = shrinkVertically(shrinkTowards = Alignment.Bottom) + fadeOut(),
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .dropShadow(
                    shadow = androidx.compose.ui.graphics.shadow.Shadow(
                        color = Color.Black.copy(alpha = 0.14f), // Adjust color and alpha for visibility
                        offset = DpOffset(0.dp, (-1).dp), // Negative Y offset for top shadow
                        radius = 8.dp,
                    ),
                    shape = RoundedCornerShape(topStart = 10.dp, topEnd = 10.dp),
                )
                .background(
                    color = AppTheme.colors.surface,
                    shape = RoundedCornerShape(topStart = 12.dp, topEnd = 12.dp),
                )
                .navigationBarsPadding(),
        ) {
            IconButton(
                modifier = Modifier
                    .padding(end = 8.dp)
                    .align(Alignment.End),
                onClick = onDoneClicked,
            ) {
                Icon(
                    painter = painterResource(Res.drawable.ic_keyboard_hide_24),
                    contentDescription = null,
                )
            }
            content()
        }
    }
}

@Composable
internal fun PickerSection(
    modifier: Modifier = Modifier,
    showDatePicker: Boolean,
    selectedDate: Instant,
    selectedAmountIndex: Int?,
    selectedAmount: AmountUiModel?,
    onTimeChanged: (Instant) -> Unit,
    onAmountChanged: (AmountUiModel) -> Unit,
) {
    val target: PickerTarget = when {
        showDatePicker -> PickerTarget.Date
        selectedAmountIndex != null -> PickerTarget.Amount(selectedAmountIndex)
        else -> PickerTarget.None
    }

    AnimatedContent(
        modifier = modifier,
        targetState = target,
        transitionSpec = { fadeIn() togetherWith fadeOut() },
        contentKey = {
            when (it) {
                is PickerTarget.None -> "none"
                is PickerTarget.Date -> "date"
                is PickerTarget.Amount -> "amount_${it.index}"
            }
        },
    ) { pickerTarget ->
        when (pickerTarget) {
            is PickerTarget.None -> Spacer(Modifier)
            is PickerTarget.Date -> {
                DatePicker(
                    modifier = Modifier.fillMaxWidth(),
                    date = selectedDate,
                    onValueChanged = onTimeChanged,
                )
            }

            is PickerTarget.Amount -> {
                if (selectedAmount != null) {
                    AmountPicker(
                        value = selectedAmount,
                        onValueChanged = onAmountChanged,
                    )
                }
            }
        }
    }
}
